//#region Libraries
import { useState } from "react";
//#endregion
//#region Services
import { requestPenalizeStudent, requestUpdatePenaltyStudent, requestValidatePenalizeInGroupReservation, requestValidateUpdatePenaltyInGroupReservation } from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import { RESOURCES_CONSTANTS } from "config/constants";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import { INIT_FORM as INIT_FORM_PENALIZE } from "views/Recursos/ResourcesLibrary/SchedulesWrapper/ReservationData/PenalizeDialog/utils/constants";
import { ACTION_TYPES, MESSAGES_VALIDATION_REQUEST_PENALIZE } from "../constants";
//#endregion

const { RESOURCE_TYPES_IDS } = RESOURCES_CONSTANTS;

const usePenalizeStudents = ({
    students: {
        studentList,
        setStudentList,
        selectedStudents,
        setSelectedStudents
    },
    dialogs: {
        dialogShowNotActionForHavingAttendanceOrPenalty
    },
    penalizeData,
    setActionTypeOfNoActionForHaveAttendanceOrPenalty,
    addActionTypeToListActions,
    loadingRequest
}) => {
    //#region Custom Hooks
    const dialogShowPenalizeDialog = useShowDialogAlert();
    const dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties = useShowDialogAlert();
    //#endregion
    //#region States
    const [penalizeForm, setPenalizeForm] = useState({
        value: { ...INIT_FORM_PENALIZE },
        update: null
    });
    const [studentsWithRegisteredPenalty, setStudentsWithRegisteredPenalty] = useState([]);
    const [formList, setFormList] = useState([]);
    //#endregion
    //#region Functions
    const setPenalizeUpdateForm = value => {
        setPenalizeForm(prev => ({
            ...prev,
            update: value
        }));
    }
    const setPenalizeValueForm = value => {
        setPenalizeForm(prev => ({
            ...prev,
            value: typeof value === "function" 
                ? value(prev.value)
                : {
                    ...prev.value,
                    ...value
                }
        }));
    }
    const isValidPenalize = async () => {
        loadingRequest.show("Validando...");
        const newStudentsWithRegisteredPenalty = await requestValidatePenalizeInGroupReservation({ 
            reservationDetailIds: selectedStudents.map(({ reservationDetailId }) => reservationDetailId),
            resourceTypeId: RESOURCE_TYPES_IDS.GROUP_ROOM
        });
        loadingRequest.hide();
        setStudentsWithRegisteredPenalty(newStudentsWithRegisteredPenalty);
        return newStudentsWithRegisteredPenalty.length === 0;
    }
    const selectedStudentsHaveTheSamePenaltiesWhenPenaltyWillBeUpdate = () => {
        // Verificando si existen estudiantes seleccionados no guardados y no tienen sanciones diferentes
        const formsFound = formList.filter(({ studentIds }) => selectedStudents.some(({ id }) => studentIds.includes(id)));
        if (!formsFound.length) 
            return { 
                continueValidation: false, 
                message: null 
            };
        const haveTheSamePenalties = formsFound.every(({ content: currentContent }) => {
            const { content } = formsFound[0]; 
            return (
                content.penalizeDays === currentContent.penalizeDays &&
                content.reasonForPenalizeId === currentContent.reasonForPenalizeId
            );
        });
        return { 
            stopValidation: true, 
            message: haveTheSamePenalties ? null : MESSAGES_VALIDATION_REQUEST_PENALIZE.UPDATE_PENALTY 
        };
    }
    const isValidUpdatePenalty = async () => {
        // Si solo hay uno seleccionado entonces no hace falta validar
        if (selectedStudents.length === 1) 
            return null;
        let validation = selectedStudentsHaveTheSamePenaltiesWhenPenaltyWillBeUpdate();
        if (validation.stopValidation) 
            return validation.message;
        loadingRequest.show("Validando...");
        const message = await requestValidateUpdatePenaltyInGroupReservation({ 
            studentPenalizedIds: selectedStudents.map(({ penalty }) => penalty.studentPenalizedId),
            resourceTypeId: RESOURCE_TYPES_IDS.GROUP_ROOM
        });
        loadingRequest.hide();
        return message;
    }
    const getIsForUpdatePenalty = () => {
        if (!selectedStudents.length) return false;
        return selectedStudents.every(({ penalty, forPenalize }) => penalty || forPenalize);
    }
    const fillFormList = isUpdate => {
        let newFormList = formList
            // Filtrando estudiantes seleccionados de la lista de formularios
            .map(form => {
                // Si ningunos de los estudiantes seleccionados está en el lista de formularios
                if (!selectedStudents.some(({ id }) => form.studentIds.includes(id)))
                    return form;
                return {
                    ...form,
                    studentIds: form.studentIds.filter(id => !selectedStudents.some(currentStudent => currentStudent.id === id)),
                    reservationDetailIds: form.reservationDetailIds.filter(id => !selectedStudents.some(currentStudent => currentStudent.reservationDetailId === id)),
                    studentPenalizedIds: form.studentPenalizedIds.filter(id => !selectedStudents.some(currentStudent => currentStudent.penalty.studentPenalizedId === id)),
                };
            })
            // Eliminando formularios sin estudiantes
            .filter(form => form.studentIds.length);
        // Agregando nuevo formulario
        newFormList = [...newFormList, { 
            content: { ...penalizeForm.value },
            studentIds: selectedStudents.map(({ id }) => id),
            reservationDetailIds: selectedStudents
                .filter(({ penalty }) => !penalty)
                .map(({ reservationDetailId  }) => reservationDetailId), 
            studentPenalizedIds: !isUpdate 
                ? [] 
                : selectedStudents
                .filter(({ penalty }) => penalty)
                .map(({ penalty }) => penalty.studentPenalizedId)
        }];
        setFormList(newFormList);
    }
    const getInfoTooltipIfExistStudentsInFormList = (reasonsForPenalize, studentId) => {
        const formFound = formList.find(({ studentIds }) => studentIds.includes(studentId));
        if (!formFound) return null;
        const { content } = formFound;
        const reasonForPenalizeFound = reasonsForPenalize.find(({ id }) => content.reasonForPenalizeId === id);
        return {
            reason: reasonForPenalizeFound?.name,
            penalizeDays: content.penalizeDays,
            details: content.details || "-"
        }; 
    }
    const getInfoTooltipOfPenalize = ({ studentId, penalty }) => {
        const { reasonsForPenalize } = penalizeData;
        let result = getInfoTooltipIfExistStudentsInFormList(reasonsForPenalize, studentId);
        if (result) 
            return result;
        const reasonForPenalizeFound = reasonsForPenalize.find(({ id }) => penalty?.reasonForPenalizeId === id);
        result = {
            reason: reasonForPenalizeFound?.name,
            penalizeDays: penalty?.penalizeDays,
            details: penalty?.details || "-"
        };
        return result;
    }
    const openPenalizeStudentDialog = async () => {
        const isForUpdatePenalty = getIsForUpdatePenalty();
        if (isForUpdatePenalty) {
            // Si es actualización de sanción
            const validationMessage = await isValidUpdatePenalty();
            if (validationMessage === MESSAGES_VALIDATION_REQUEST_PENALIZE.UPDATE_PENALTY) {
                dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties.set(true);
                return;
            }
            const formFound = formList.find(({ studentIds }) => studentIds.includes(selectedStudents[0].id));
            setPenalizeUpdateForm(formFound ? { ...formFound.content } : { ...selectedStudents[0].penalty });
        }
        else {
            // Si es creación de sanción
            const isValid = await isValidPenalize();
            if (!isValid) {
                dialogShowNotActionForHavingAttendanceOrPenalty.set(true);
                setActionTypeOfNoActionForHaveAttendanceOrPenalty(ACTION_TYPES.PENALIZE);
                return;
            }
            setPenalizeUpdateForm(null);
        }
        dialogShowPenalizeDialog.set(true);
    }
    const penalizeStudentFromReservationList = isUpdate => {
        dialogShowPenalizeDialog.set(false);
        setStudentList(studentList.map(student => {
            if (selectedStudents.some(({ id }) => id === student.id))
                return {
                    ...student,
                    forPenalize: true
                };
            return student;
        }));
        fillFormList(isUpdate);
        setSelectedStudents([]);
        addActionTypeToListActions(ACTION_TYPES.PENALIZE);
    }
    const doRequestPenalizeStudents = async () => {
        for (const currentForm of formList) {
            const isUpdate = Boolean(currentForm.studentPenalizedIds.length);
            // Realizando petición de sanción a los estudiantes seleccionados
            const params = isUpdate 
                ? {
                    loadingRequestMessage: "Editando sanción..." ,
                    request: requestUpdatePenaltyStudent
                } : {
                    loadingRequestMessage: "Sancionando alumno(s)...",
                    request: requestPenalizeStudent
                };
            loadingRequest.show(params.loadingRequestMessage);
            const { 
                content, 
                reservationDetailIds, 
                studentPenalizedIds 
            } = currentForm;
            const success = await params.request({
                resourceTypeId: RESOURCE_TYPES_IDS.GROUP_ROOM,
                ...content,
                reservationDetailIds,
                studentPenalizedIds
            });
            loadingRequest.hide();
            if (!success) 
                throw new Error(true);
        }
    }
    //#endregion
    return {
        penalizeForm,
        formList,
        dialogs: {
            dialogShowPenalizeDialog,
            dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties
        },
        fns: {
            openPenalizeStudentDialog,
            setPenalizeValueForm,
            penalizeStudentFromReservationList,
            doRequestPenalizeStudents,
            getIsForUpdatePenalty,
            getInfoTooltipOfPenalize
        },
        studentsWithRegisteredPenalty
    };
}	
    
export default usePenalizeStudents;